import type {
  FetchResponse,
  RestService,
  BaseParams,
  BodyParams,
  ClassParams,
  DelegationParams,
  MethodParams,
  ObjectParams,
  QueryParams,
} from '@sydle/ui-api';
import { buildRequestParams } from '@sydle/ui-api';
import type { EObject } from '../models/EObject';
import type { Metadata } from '../models/Metadata';
import type { Page } from '../models/Page';
import { ObjectDelegationInterceptor } from './delegation/object-delegation-interceptor';
import { DraftMethods } from './draft-api';
import type { Reference } from '../models';

export type PatchOperation = { op: string; value: any; path: string; from?: string };
export type GetParams = BaseParams & ClassParams & ObjectParams;
export type GetDraftsParams = BaseParams & ClassParams & Partial<ObjectParams> & Partial<BodyParams>;
export type GetHistoryParams = BaseParams & ClassParams & ObjectParams & { size: number; page: number };
export type CreateParams = BaseParams & ClassParams & { eObject: any };
export type DuplicateParams = BaseParams & ClassParams & { eObject: any };
export type UpdateParams = BaseParams &
  ClassParams &
  Partial<DelegationParams> &
  ObjectParams &
  Partial<QueryParams> & { eObject: any };
export type PatchParams = BaseParams &
  ClassParams &
  Partial<DelegationParams> &
  ObjectParams &
  Partial<QueryParams> & { ignoreResponseBody?: any; operations: PatchOperation[] };
export type GetMetadataParams = BaseParams & ClassParams & Partial<ObjectParams> & Partial<QueryParams>;
export type GetExtendedMetadataParams = BaseParams &
  ClassParams &
  Partial<ObjectParams> &
  Partial<QueryParams> & { option: string };
export type DeleteParams = BaseParams & ClassParams & ObjectParams & Partial<DelegationParams>;
export type ExecuteCustomParams = BaseParams &
  ClassParams &
  MethodParams &
  Partial<ObjectParams> &
  Partial<BodyParams> &
  Partial<QueryParams> & { method: string } & { activeDelegationInterceptor?: boolean };

export enum ObjectMethods {
  GET = '_get',
  GET_HISTORY = '_getHistory',
  GET_HISTORY_ITEM = '_getHistoryItem',
  CREATE = '_create',
  DUPLICATE = '_duplicate',
  UPDATE = '_update',
  PATCH = '_patch',
  METADATA = '_getMetadata',
  DELETE = '_delete',
  EXECUTE = '_execute',
}

/**
 * API de objetos.
 */
export class ObjectAPI {
  private restService: RestService;

  constructor(restService: RestService, enableDelegation?: boolean) {
    this.restService = restService;
    if (enableDelegation) {
      this.restService.registerInterceptor(new ObjectDelegationInterceptor());
    }
  }

  get(args: GetParams): Promise<FetchResponse<EObject>> {
    const requestParams = buildRequestParams({ ...args, methodIdentifier: ObjectMethods.GET });

    return this.restService.get(requestParams);
  }

  getDrafts(args: GetDraftsParams): Promise<FetchResponse<EObject[]>> {
    const requestParams = buildRequestParams({
      ...args,
      methodIdentifier: DraftMethods.MULTIPLE_GET,
      queryParams: { _translate: false },
    });

    return this.restService.post(requestParams);
  }

  getHistory(args: GetHistoryParams): Promise<FetchResponse<Page>> {
    const { size, page } = args;

    const requestParams = buildRequestParams({
      ...args,
      methodIdentifier: ObjectMethods.GET_HISTORY,
      queryParams: { size, page },
    });

    return this.restService.get(requestParams);
  }

  getHistoryItem(reference: Reference): Promise<FetchResponse<EObject>> {
    const requestParams = buildRequestParams({
      ...reference,
      methodIdentifier: ObjectMethods.GET_HISTORY_ITEM,
    });

    return this.restService.get(requestParams);
  }

  create(args: CreateParams): Promise<FetchResponse<EObject>> {
    const { eObject } = args;

    const requestParams = buildRequestParams({
      ...args,
      methodIdentifier: ObjectMethods.CREATE,
      body: eObject,
    });

    return this.restService.post(requestParams);
  }

  duplicate(args: DuplicateParams): Promise<FetchResponse<EObject>> {
    const { eObject } = args;

    const requestParams = buildRequestParams({
      ...args,
      methodIdentifier: ObjectMethods.DUPLICATE,
      body: eObject,
    });

    return this.restService.post(requestParams);
  }

  update(args: UpdateParams): Promise<FetchResponse<EObject>> {
    const { eObject } = args;

    const requestParams = buildRequestParams({
      ...args,
      methodIdentifier: ObjectMethods.UPDATE,
      body: eObject,
    });

    return this.restService.post(requestParams);
  }

  patch(args: PatchParams): Promise<FetchResponse<EObject>> {
    const { operations, ignoreResponseBody } = args;

    const requestParams = buildRequestParams({
      ...args,
      methodIdentifier: ObjectMethods.PATCH,
      body: operations,
      queryParams: { ...args.queryParams, ignoreResponseBody },
    });

    return this.restService.post(requestParams);
  }

  getMetadata(args: GetMetadataParams): Promise<FetchResponse<Metadata>> {
    const requestParams = buildRequestParams({ ...args, methodIdentifier: ObjectMethods.METADATA });

    return this.restService.get(requestParams);
  }

  getExtendedMetadata(args: GetExtendedMetadataParams): Promise<FetchResponse<Metadata>> {
    const { option } = args;

    const requestParams = buildRequestParams({
      ...args,
      methodIdentifier: ObjectMethods.METADATA,
      queryParams: {
        ...args.queryParams,
        _extendedMetadata: true,
        option,
      },
    });
    return this.restService.get(requestParams);
  }

  delete(args: DeleteParams): Promise<FetchResponse<any>> {
    const requestParams = buildRequestParams({ ...args, methodIdentifier: ObjectMethods.DELETE });

    return this.restService.post(requestParams);
  }

  executeCustom(args: ExecuteCustomParams): Promise<FetchResponse<any>> {
    let { method } = args;
    method = method ? method.toLocaleLowerCase() : 'get';

    if (method == 'get') {
      const bodyAsParam = args.body ? encodeURIComponent(JSON.stringify(args.body)) : null;
      delete args.body;
      const requestParams = buildRequestParams({
        ...args,
        queryParams: {
          ...args.queryParams,
          ...(bodyAsParam ? { _body: bodyAsParam } : {}),
        },
      });
      return this.restService.get(requestParams);
    }

    const requestParams = buildRequestParams(args);

    return this.restService.post(requestParams);
  }
}
